import React, { useEffect, useState, useRef } from "react";
import { CircleCheck,ArrowRight } from "lucide-react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import appConfig from "../utils/appConfig";

const SubscriptionSection = ({
  allPricingPlan = [],
  handlePricingCardClick,
}) => {
  const [pricingPlans, setPricingPlans] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showSkeleton, setShowSkeleton] = useState(true);
  const rowRefs = useRef([]);
  const descriptionRefs = useRef([])
  const availableBillingCycles = pricingPlans.reduce((acc, ele) => {
    if (!ele?.frequency || !Array.isArray(ele.frequency)) return acc

    ele.frequency.forEach((freq) => {
      if (freq === "all") {
        acc.add("Stock Research");
        acc.add("Yearly");
        acc.add("Half-yearly");
        acc.add("Quarterly");
        acc.add("Monthly");
      }
      if (freq === "stockResearch") acc.add("Stock Research");
      if (freq === "yearly") acc.add("Yearly");
      if (freq === "halfYearly") acc.add("Half-yearly");
      if (freq === "quarterly") acc.add("Quarterly");
      if (freq === "monthly") acc.add("Monthly");
    });

    if (ele.duration) {
      if (ele.duration === 2) acc.add("Stock Research"); // Handle 0 duration
      if (ele.duration === 365) acc.add("Yearly");
      if (ele.duration === 180) acc.add("Half-yearly");
      if (ele.duration === 90) acc.add("Quarterly");
      if (ele.duration === 30) acc.add("Monthly");
    }

    return acc;
  }, new Set());

  // Ensure available billing cycles are in the correct order
  const billingCycles = [
    "Stock Research",
    "Yearly",
    "Half-yearly",
    "Quarterly",
    "Monthly",
  ].filter((cycle) => availableBillingCycles.has(cycle));

  // ✅ Set activeCycle to the first available billing cycle
  const [activeCycle, setActiveCycle] = useState(billingCycles[0] || "Monthly")

  const sortPricingPlans = (plans) => {
    return plans.sort((a, b) => {
      const aPrice =
        a.amount ||
        (a.pricing && (a.pricing.monthly || a.pricing.quarterly || a.pricing.halfYearly || a.pricing.yearly)) ||
        0
      const bPrice =
        b.amount ||
        (b.pricing && (b.pricing.monthly || b.pricing.quarterly || b.pricing.halfYearly || b.pricing.yearly)) ||
        0
      return bPrice - aPrice
    })
  }

  useEffect(() => {
    if (allPricingPlan.length > 0) {
      setPricingPlans(sortPricingPlans(allPricingPlan))
      setIsLoading(false)
      setShowSkeleton(false) // Stop showing the skeleton when data is available
    } else {
      const timer = setTimeout(() => {
        setShowSkeleton(false) // Hide skeleton after 3 seconds
        setIsLoading(false)
      }, 3000)

      return () => clearTimeout(timer) // Cleanup timer on component unmount
    }
  }, [allPricingPlan])

  useEffect(() => {
    if (!isLoading) {
      adjustCardHeights()

      // Add resize listener to readjust heights on window resize
      window.addEventListener("resize", adjustCardHeights)
      return () => {
        window.removeEventListener("resize", adjustCardHeights)
      }
    }
  }, [isLoading, pricingPlans, activeCycle])

  useEffect(() => {
    const h1 = document.querySelector("#subscriptionSection h1")
    if (h1) {
      h1.style.fontSize = "28px"
      h1.style.fontWeight = "bold"
      h1.style.textAlign = "center"
      h1.style.marginTop = "1rem"
      h1.style.paddingLeft = "1rem"
      h1.style.paddingRight = "1rem"
      h1.style.lineHeight = "1.2"

      const updateH1Style = () => {
        if (window.innerWidth >= 640) {
          h1.style.fontSize = "30px"
          h1.style.marginTop = "1.5rem"
        }
        if (window.innerWidth >= 768) {
          h1.style.fontSize = "36px"
          h1.style.marginTop = "2rem"
        }
      }

      updateH1Style()
      window.addEventListener("resize", updateH1Style)

      return () => {
        window.removeEventListener("resize", updateH1Style)
      }
    }
  }, [])

  const adjustCardHeights = () => {
    // Reset heights first
    descriptionRefs.current.forEach((container) => {
      if (container) {
        container.style.height = "auto"
      }
    })

    const rows = []
    let currentRow = []
    rowRefs.current.forEach((card, index) => {
      if (card) {
        currentRow.push(card)
        if ((index + 1) % 3 === 0 || index === rowRefs.current.length - 1) {
          rows.push(currentRow)
          currentRow = []
        }
      }
    })

    rows.forEach((row) => {
      const descriptionContainers = row.map((card) => card.querySelector(".description-container"))
      const hasDescription = descriptionContainers.some((container) => container?.textContent?.trim().length > 0)

      if (hasDescription) {
        const maxHeight = Math.max(...descriptionContainers.map((container) => container?.scrollHeight || 0))
        descriptionContainers.forEach((container) => {
          if (container) {
            container.style.height = `${maxHeight}px`
          }
        })
      } else {
        descriptionContainers.forEach((container) => {
          if (container) {
            container.style.height = "auto"
          }
        })
      }
    })
  }

  const formatDescription = (description) => {
    if (!description) return ["No description available."]
    return description
      .split(".")
      .filter((sentence) => sentence.trim() !== "")
      .map((sentence) => sentence.trim() + ".")
  }

  const SkeletonCard = () => (
    <div className="w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] max-w-[350px] relative font-poppins min-h-[200px] rounded-xl bg-white border-[1px] border-[#000000]/10 shadow-[0px_4px_4px_0px_rgba(0,0,0,0.06)] py-4">
      <div className="flex items-start pl-7 pt-2 pb-7 md:items-center">
        <div className="overflow-hidden rounded-lg">
          <Skeleton width={128} height={25} />
          <Skeleton width={100} height={30} />
        </div>
      </div>
      <div className="flex justify-center items-center">
        <div className="w-[80%] border-t-[1px] border-b-[1px] border-[#000000]/10"></div>
      </div>
      <div className="flex flex-col items-center pt-7 p-4 space-y-4 w-90">
        <div className="font-poppins text-[12.5px] min-h-[220px] flex flex-col items-left justify-start space-y-2 text-[#666666]">
          <Skeleton width={200} height={5} />
          <Skeleton width={200} height={5} />
          <Skeleton width={200} height={5} />
          <Skeleton width={200} height={5} />
          <Skeleton width={200} height={5} />
        </div>
      </div>
      <div className="p-4">
        <div className="flex justify-center align">
          <Skeleton width={280} height={44} />
        </div>
      </div>
    </div>
  )

  const filteredPlans = pricingPlans.filter((ele) => {
    if (!ele?.frequency || !Array.isArray(ele.frequency)) return false

    // Check if the selected cycle exists inside the frequency array
    if (ele.frequency.includes("all")) return true // Show in all tabs
    if (ele.frequency.includes("monthly") && activeCycle === "Monthly") return true
    if (ele.frequency.includes("quarterly") && activeCycle === "Quarterly") return true
    if (ele.frequency.includes("halfYearly") && activeCycle === "Half-yearly") return true
    if (ele.frequency.includes("yearly") && activeCycle === "Yearly") return true

    // If `duration` exists, map it accordingly
    if (ele.duration) {
      if (ele.duration === 2 && activeCycle === "Stock Research") return true
      if (ele.duration === 30 && activeCycle === "Monthly") return true
      if (ele.duration === 90 && activeCycle === "Quarterly") return true
      if (ele.duration === 180 && activeCycle === "Half-yearly") return true
      if (ele.duration === 365 && activeCycle === "Yearly") return true
    }

    return false
  })

  if (!showSkeleton && pricingPlans?.length === 0) {
    return null // Return null after 3 seconds if no data
  }

  return (
    <div className="pt-12 pb-32 bg-gradient-to-b from-blue-50 to-white" id="subscriptionSection">
           <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 text-center mb-4 sm:mb-10">
           Stock Recommendations To Elevate Your Portfolio</h1>

      <div className="px-4 sm:px-0 mb-8 mt-5">
        <div className="relative overflow-x-auto pb-1 -mx-4 px-4 sm:mx-0 sm:px-0">
          <div className="flex items-center justify-start sm:justify-center border-b border-gray-200 w-max sm:w-auto mx-auto">
            {billingCycles.map((cycle) => (
              <button
                key={cycle}
                onClick={() => setActiveCycle(cycle)}
                className={`whitespace-nowrap py-2 px-3 sm:px-4 text-sm sm:text-lg font-semibold border-b-2 transition-colors ${
                  activeCycle === cycle
                    ? "text-blue-700 border-blue-700"
                    : "text-gray-500 border-transparent hover:text-blue-600 hover:border-blue-300"
                }`}
              >
                {cycle}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-20 pt-10 md:pt-14 lg:pt-14">
        <div className="flex flex-wrap justify-center gap-6">
          {isLoading ? (
            Array(3)
              .fill(0)
              .map((_, i) => <SkeletonCard key={i} />)
          ) : filteredPlans.length > 0 ? (
            filteredPlans
              ?.filter((ele) => ele?.name !== "priorRecommendationPlan")
              ?.map((ele, i) => (
                <div
                  key={i}
                  ref={(el) => (rowRefs.current[i] = el)}
                  className="relative w-[350px] font-poppins rounded-xl bg-white border-[1px] border-blue-200 shadow-[0px_4px_12px_0px_rgba(59,130,246,0.1)] py-4 cursor-pointer overflow-hidden flex flex-col"
                  onClick={ele?.subscription ? undefined : () => handlePricingCardClick(ele)}
                >
                  {/* Subtle background accent */}
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-transparent opacity-40"></div>

                  <div className="flex items-start pl-7 pt-2 pb-7 md:items-center relative z-10">
                    <div className="overflow-hidden rounded-lg w-full">
                      <h2
                        className="font-poppins text-[22px] font-bold leading-[35px] text-left text-[#333333] truncate"
                        title={ele?.name}
                      >
                        {ele?.name}
                      </h2>
                      <div className="flex items-baseline">
                        <h2 className="text-3xl font-bold text-black">
                          ₹
                          {ele?.amount ||
                            (ele?.pricing &&
                              (ele.pricing.monthly ||
                                ele.pricing.quarterly ||
                                ele.pricing.halfYearly ||
                                ele.pricing.yearly)) ||
                            "N/A"}{" "}
                          {process.env.REACT_APP_AdVISOR_GST_CONFIGURE === "true" ? "+ GST" : ""}
                        </h2>
                        <p className="text-[16px] text-[#95989C] font-normal ml-1">/{activeCycle}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-start items-start pl-7 relative z-10">
                    <div className="w-[80%] border-t-[1px] border-b-[1px] border-blue-100"></div>
                  </div>
                  <div className="flex flex-col items-start pt-7 p-4 pb-16 space-y-2 w-full relative z-10 flex-grow">
                    <div className="flex w-full mb-2">
                      <CircleCheck size={24} className="text-blue-600 mr-2 flex-shrink-0" />
                      <div className="flex flex-col w-full">
                        <h4 className="text-md font-bold font-poppins text-gray-900 pb-2 whitespace-nowrap overflow-hidden text-ellipsis">
                          {ele.amount === 9999 ? "Offerings + Value-Added Benefits" : "Offerings"}
                        </h4>
                      </div>
                    </div>
                    <div className="flex flex-col items-start px-7 w-full">
                      <div
                        ref={(el) => (descriptionRefs.current[i] = el)}
                        className="font-poppins text-[12.5px] flex flex-col items-start space-y-2 text-[#666666] description-container w-full whitespace-pre-line overflow-y-auto"
                      >
                        {ele?.description}
                      </div>
                    </div>
                  </div>

                  <div className="w-full bottom-0 p-4 flex justify-center align relative z-10">
  <button
    className={`group w-full py-2.5 px-4 ${appConfig.colors.button} flex items-center justify-center rounded-lg font-medium text-sm text-white relative overflow-hidden transition-all duration-300 bg-gradient-to-r from-blue-700 via-blue-500 to-blue-700 hover:-translate-y-0.5 shadow-md hover:shadow-blue-300/30 bg-[length:200%_100%] bg-[position:0%_0%] hover:bg-[position:100%_0%]`}
  >
    <span className="relative z-10 flex items-center justify-center">
      {ele?.subscription ? "Subscribed" : "Invest Now"}
      <ArrowRight className="w-4 h-4 ml-1.5 transition-transform duration-300 group-hover:translate-x-1" />
    </span>
  </button>
</div>


                  {/* Decorative corner accent */}
                  <div className="absolute top-0 right-0 w-24 h-24 bg-blue-100 rounded-bl-full -mr-12 -mt-12 opacity-50"></div>
                </div>
              ))
          ) : (
            <p className="text-center text-gray-600">No plans available.</p>
          )}
        </div>
      </div>
    </div>
  )
}

export default SubscriptionSection
